import { useState } from "react";
import { CombinedGraphQLErrors } from "@apollo/client/errors";

import { mintClientRequestId } from "./noteDraft";
import type { RecipientEntry } from "./recipients";

/**
 * Draft and idempotency-key rules of "An Kollegen senden" (#717, D-15, UI-SPEC "Draft handling").
 *
 * A draft is what the dialog holds for one message: recipients, text, the attachment choice and
 * the key the send travels under. It lives in memory for as long as the ticket view is mounted
 * (closing and reopening the dialog restores it, a reload does not), never in browser storage:
 * the recipient list names colleagues and outside addresses.
 *
 * The key is minted when the draft is created and reused for every retry of an unchanged
 * payload, so a retried send is one mail and one entry on the server. After a FAILED send, a
 * changed recipient list, text or attachment choice gets a new key: the cost is a possible
 * visible duplicate, the alternative would be to drop the edit.
 */

export const MAX_FORWARD_TEXT_CHARS = 5000;

/**
 * The length of a text as the server counts it (`clean_forward_text`): code points, after NUL
 * characters are dropped, line endings are normalised and the ends are trimmed. One emoji is one,
 * and the whitespace around the text is not counted. The counter and the limit check both use it.
 */
export function forwardTextLength(text: string): number {
  return Array.from(
    text.replace(/\0/g, "").replace(/\r\n?/g, "\n").trim(),
  ).length;
}

export interface ForwardDraft {
  recipients: RecipientEntry[];
  text: string;
  includeAttachments: boolean;
  /** The key the next send of this draft goes under. */
  clientRequestId: string;
  /** The payload signature of the last send that failed; `null` while none failed under this key. */
  signature: string | null;
}

/** A fresh draft: nothing typed, attachments on (D-08), a new key. */
export function newForwardDraft(
  mint: () => string = mintClientRequestId,
): ForwardDraft {
  return {
    recipients: [],
    text: "",
    includeAttachments: true,
    clientRequestId: mint(),
    signature: null,
  };
}

/**
 * A stable string for what a send carries: who (by account or lower-cased address, in any
 * order), the text and the attachment choice. Equal payloads have equal signatures.
 */
export function payloadSignature(
  draft: Pick<ForwardDraft, "recipients" | "text" | "includeAttachments">,
): string {
  const who = draft.recipients
    .map((entry) =>
      entry.userId
        ? `user:${entry.userId}`
        : `address:${(entry.address ?? entry.label).trim().toLowerCase()}`,
    )
    .sort();
  return JSON.stringify([who, draft.text, draft.includeAttachments]);
}

/**
 * The key to send `draft` under. A first send, and a retry of an unchanged payload after a
 * failure, reuse the draft's key; a payload changed after a failed send gets a new key and a
 * clean failure record.
 */
export function keyForForwardSubmit(
  draft: ForwardDraft,
  mint: () => string = mintClientRequestId,
): { clientRequestId: string; draft: ForwardDraft } {
  if (draft.signature !== null && draft.signature !== payloadSignature(draft)) {
    const next: ForwardDraft = {
      ...draft,
      clientRequestId: mint(),
      signature: null,
    };
    return { clientRequestId: next.clientRequestId, draft: next };
  }
  return { clientRequestId: draft.clientRequestId, draft };
}

/** The draft after a send failed: the payload it carried is remembered, the key stays. */
export function afterForwardFailure(draft: ForwardDraft): ForwardDraft {
  return { ...draft, signature: payloadSignature(draft) };
}

/**
 * How the dialog words a refusal: the message is gone, the mail too large, the key was used for
 * another payload (the server resolved the send differently from the first try), a recipient
 * would need a mail while sending by mail is off, anything else.
 */
export type ForwardErrorKind =
  | "gone"
  | "tooLarge"
  | "keyReused"
  | "mailUnavailable"
  | "failed";

/** Maps the server's `extensions.code` (jupiter/services/forwards.py); a network error is "failed". */
export function forwardErrorKind(error: unknown): ForwardErrorKind {
  if (CombinedGraphQLErrors.is(error)) {
    const code: unknown = error.errors[0]?.extensions?.code;
    if (code === "FORWARD_MESSAGE_GONE") return "gone";
    if (code === "FORWARD_TOO_LARGE") return "tooLarge";
    if (code === "FORWARD_KEY_REUSED") return "keyReused";
    if (code === "FORWARD_MAIL_UNAVAILABLE") return "mailUnavailable";
  }
  return "failed";
}

/**
 * The draft after a send failed with `kind`. The payload is remembered and the key stays, so an
 * unchanged retry is the same send; except after `keyReused`: the server refuses that key for
 * this payload for good, so the draft gets a new key and no failure record, and the next click
 * is a fresh send (as the copy "Schließe den Dialog und sende neu" promises).
 */
export function afterForwardRefusal(
  draft: ForwardDraft,
  kind: ForwardErrorKind,
  mint: () => string = mintClientRequestId,
): ForwardDraft {
  if (kind === "keyReused") {
    return { ...draft, clientRequestId: mint(), signature: null };
  }
  return afterForwardFailure(draft);
}

export interface ForwardDrafts {
  /** The draft of a message; created (with a new key) on first use. */
  get: (messageId: string) => ForwardDraft;
  set: (messageId: string, draft: ForwardDraft) => void;
  /** A successful send discards the draft: the next open starts empty with a new key. */
  clear: (messageId: string) => void;
}

/**
 * One draft per message id for the life of the host component (the ticket view). The store is
 * mutable on purpose and not React state: nothing renders from it, the dialog reads it once
 * when it opens and writes through on every change.
 */
export function useForwardDrafts(): ForwardDrafts {
  const [store] = useState<ForwardDrafts>(() => {
    const drafts = new Map<string, ForwardDraft>();
    return {
      get(messageId) {
        let draft = drafts.get(messageId);
        if (!draft) {
          draft = newForwardDraft();
          drafts.set(messageId, draft);
        }
        return draft;
      },
      set(messageId, draft) {
        drafts.set(messageId, draft);
      },
      clear(messageId) {
        drafts.delete(messageId);
      },
    };
  });
  return store;
}
